import { Action, createReducer, on } from "@ngrx/store";
import cloneDeep from "lodash-es/cloneDeep";
import { VoucherState } from "./voucher";
import { APP_INIT_STATES } from "../status/status";
import {
  checkVoucher,
  checkVoucherError,
  checkVoucherSuccess,
  cancelVoucher,
  cancelVoucherError,
  cancelVoucherSuccess,
  confirmVoucher,
  confirmVoucherError,
  confirmVoucherSuccess,
  reserveVoucher,
  reserveVoucherError,
  reserveVoucherSuccess,
  resetVoucher,
  selectVoucherPharmacy,
  setVoucherAvailability,
  startNextVoucher,
} from "./voucher.actions";
import { loadDashboardSuccess } from "../user/user.actions";
import { environment } from "../../../environments/environment";

export const initialState: VoucherState = {
  enabled: false,
  availabilityLoaded: false,
  pharmacies: [],
  selectedPharmacy: null,
  voucher: null,
  reservation: null,
  redemption: null,
  history: [],
  idempotencyKey: null,
  checkStatus: APP_INIT_STATES.INIT,
  reserveStatus: APP_INIT_STATES.INIT,
  confirmStatus: APP_INIT_STATES.INIT,
  cancelStatus: APP_INIT_STATES.INIT,
  error: null,
};

/** Setzt den Vorgang zurück, die Apothekenauswahl bleibt unberührt. */
function clearProcess(state: VoucherState): VoucherState {
  state.voucher = null;
  state.reservation = null;
  state.redemption = null;
  state.history = [];
  state.idempotencyKey = null;
  state.checkStatus = APP_INIT_STATES.INIT;
  state.reserveStatus = APP_INIT_STATES.INIT;
  state.confirmStatus = APP_INIT_STATES.INIT;
  state.cancelStatus = APP_INIT_STATES.INIT;
  state.error = null;

  return state;
}

const reducer = createReducer(
  initialState,
  /**
   * Die Freischaltung reist im `jolioo`-Block der Dashboard-Antwort mit — es
   * gibt dafür keinen eigenen Request.
   */
  on(loadDashboardSuccess, (state, action) => {
    const jolioo = action.response?.jolioo;

    state = cloneDeep(state);
    // ⚠ Auch ohne jolioo-Block gilt die Verfügbarkeit als geklärt — sonst
    // bliebe die Seite ewig im Ladezustand.
    state.availabilityLoaded = true;

    if (!jolioo) {
      // ⚠ TEMPORAER — nur bis apo_jolioo auf develop deployt ist. Ohne den
      // `jolioo`-Block bleibt der Flow sonst unerreichbar und der Kamera-Scan
      // nicht testbar. Greift ausschliesslich, wenn das Backend gar nichts
      // liefert — sobald es antwortet, gewinnt immer die echte Antwort.
      if (environment.voucherDebug) {
        state.enabled = true;
        state.pharmacies = [
          {
            id: "debug-pharmacy",
            name: "Test-Apotheke (nur Entwicklung)",
            pharmacy_number: "000000",
          },
        ];
        state.selectedPharmacy = "debug-pharmacy";
      }

      return state;
    }

    const previous = state.pharmacies.map((p) => p.id).join("|");

    state.enabled = !!jolioo.enabled;
    state.pharmacies = jolioo.pharmacies ?? [];

    const current = state.pharmacies.map((p) => p.id).join("|");

    if (state.pharmacies.length === 1) {
      state.selectedPharmacy = state.pharmacies[0].id;
    } else if (
      current !== previous &&
      !state.voucher &&
      !state.pharmacies.some((p) => p.id === state.selectedPharmacy)
    ) {
      // ⚠ Nur wenn die Auswahl nicht mehr in der Liste steht — sonst löschte die
      // erste Dashboard-Antwort nach einem Neuladen die wiederhergestellte
      // Auswahl. ⚠ Nicht mitten im Vorgang: dort hängen Bestätigen und Abbrechen
      // der Reservierung daran.
      state.selectedPharmacy = null;
    }

    return state;
  }),
  on(setVoucherAvailability, (state, action) => {
    state = cloneDeep(state);
    state.availabilityLoaded = true;
    state.enabled = action.enabled;
    state.pharmacies = action.pharmacies;

    // Bei genau einer Apotheke entfällt die Auswahl.
    if (action.pharmacies.length === 1) {
      state.selectedPharmacy = action.pharmacies[0].id;
    }

    return state;
  }),
  on(selectVoucherPharmacy, (state, action) => {
    state = cloneDeep(state);
    state.selectedPharmacy = action.pharmacy;

    return state;
  }),
  on(checkVoucher, (state) => {
    state = cloneDeep(state);
    state.checkStatus = APP_INIT_STATES.LOADING;
    state.voucher = null;
    state.error = null;

    return state;
  }),
  on(checkVoucherSuccess, (state, action) => {
    state = cloneDeep(state);
    state.voucher = action.voucher;
    // Der Verlauf kommt mit der Pruefung — kein eigener Aufruf.
    state.history = action.voucher.history ?? [];
    state.checkStatus = APP_INIT_STATES.SUCCESS;

    return state;
  }),
  on(checkVoucherError, (state, action) => {
    state = cloneDeep(state);
    state.checkStatus = APP_INIT_STATES.ERROR;
    state.error = action.error;

    return state;
  }),
  on(reserveVoucher, (state, action) => {
    state = cloneDeep(state);
    state.reserveStatus = APP_INIT_STATES.LOADING;
    // ⚠ Ein Status, der auf SUCCESS stehen bleibt, schickt die zugehoerige
    // Route beim naechsten Betreten sofort weiter.
    state.confirmStatus = APP_INIT_STATES.INIT;
    state.cancelStatus = APP_INIT_STATES.INIT;
    state.idempotencyKey = action.idempotencyKey;
    state.error = null;

    return state;
  }),
  on(reserveVoucherSuccess, (state, action) => {
    state = cloneDeep(state);
    state.reservation = action.reservation;
    state.voucher = action.reservation.voucher;
    state.reserveStatus = APP_INIT_STATES.SUCCESS;

    return state;
  }),
  on(reserveVoucherError, (state, action) => {
    state = cloneDeep(state);
    state.reserveStatus = APP_INIT_STATES.ERROR;
    state.error = action.error;
    // ⚠ idempotencyKey bleibt stehen, damit ein Retry denselben Key verwendet.

    return state;
  }),
  on(confirmVoucher, (state) => {
    state = cloneDeep(state);
    state.confirmStatus = APP_INIT_STATES.LOADING;
    state.error = null;

    return state;
  }),
  on(confirmVoucherSuccess, (state, action) => {
    state = cloneDeep(state);
    state.redemption = action.redemption;
    state.voucher = action.redemption.voucher;
    state.confirmStatus = APP_INIT_STATES.SUCCESS;
    state.reserveStatus = APP_INIT_STATES.INIT;
    state.reservation = null;
    // Vorgang abgeschlossen — ein neuer bekommt einen neuen Key.
    state.idempotencyKey = null;

    return state;
  }),
  on(confirmVoucherError, (state, action) => {
    state = cloneDeep(state);
    state.confirmStatus = APP_INIT_STATES.ERROR;
    state.error = action.error;

    return state;
  }),
  on(cancelVoucher, (state) => {
    state = cloneDeep(state);
    state.cancelStatus = APP_INIT_STATES.LOADING;
    state.error = null;

    return state;
  }),
  on(cancelVoucherError, (state, action) => {
    state = cloneDeep(state);
    state.cancelStatus = APP_INIT_STATES.ERROR;
    state.error = action.error;
    // ⚠ Reservierung bleibt stehen — sie besteht bei Jolioo ja weiter.

    return state;
  }),
  on(cancelVoucherSuccess, (state, action) => {
    state = cloneDeep(state);
    state.cancelStatus = APP_INIT_STATES.SUCCESS;
    state.reservation = null;
    // ⚠ Ohne das bleibt der wähend der Reservierung reduzierte Restwert stehen
    // und der Gutschein wirkt nach dem Abbruch kleiner, als er ist.
    state.voucher = action.voucher;
    state.reserveStatus = APP_INIT_STATES.INIT;
    state.confirmStatus = APP_INIT_STATES.INIT;
    state.idempotencyKey = null;

    return state;
  }),
  on(resetVoucher, (state) => {
    state = cloneDeep(state);
    // ⚠ Auch die Auswahl: ein neuer Besuch startet bewusst ohne Vorauswahl,
    // sonst wird auf die zuletzt benutzte Apotheke gebucht, ohne dass jemand waehlt.
    state.selectedPharmacy =
      state.pharmacies.length === 1 ? state.pharmacies[0].id : null;

    return clearProcess(state);
  }),
  on(startNextVoucher, (state) => clearProcess(cloneDeep(state))),
);

export function VoucherReducer(
  state: VoucherState | undefined,
  action: Action,
) {
  return reducer(state, action);
}
